import CustomButton from "@/shared/components/kit/button";
import StackKit from "@/shared/components/kit/stack";
import TypoGraphyKit from "@/shared/components/kit/typoGraphy";
import {
  CircularProgress,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
} from "@mui/material";
import { Dispatch, SetStateAction, useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { AxiosError } from "axios";
import ForgetPasswordOtp from "./forgetPasswordOtp";
import { useMutation } from "@tanstack/react-query";
import { postRequestResetPass } from "@/api/user/home";
import { toast } from "react-toastify";
import {
  toEnglishNumber,
  toPersianNumber,
} from "@/hooks/useThousandsSeparator";

const formSchema = z.object({
  username: z.string().min(1, "نام کاربری الزامی است"),
  phone: z.string().min(1, "رمز عبور الزامی است"),
});

type FormData = z.infer<typeof formSchema>;

// Constants for localStorage keys
const STORAGE_KEYS = {
  USER_TYPE: "userType",
} as const;

// User type options
const USER_TYPES = {
  USER: "user",
  MERCHANT: "merchant",
} as const;

const FormForgetPassword = ({
  setIsRegister,
  setIsForgetPassword,
}: {
  setIsRegister: Dispatch<SetStateAction<boolean>>;
  setIsForgetPassword: Dispatch<SetStateAction<boolean>>;
}) => {
  const [valueUserName, setValueUserName] = useState("");
  const [valuePhone, setValuePhone] = useState("");
  const [serverError, setServerError] = useState<string | null>(null);
  const [stepTwo, setStepTwo] = useState(false);
  const [selectedOption, setSelectedOption] = useState<string | null>(() => {
    const savedUserType = localStorage.getItem(STORAGE_KEYS.USER_TYPE);
    return savedUserType || USER_TYPES.USER;
  });
  const [userData, setUserData] = useState({ phone: "", nID: "" });

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
  });

  // Save to localStorage whenever selectedOption changes
  useEffect(() => {
    if (selectedOption) {
      localStorage.setItem(STORAGE_KEYS.USER_TYPE, selectedOption);
    }
  }, [selectedOption]);

  const {
    mutate: fetchRequestResetPass,
    isPending,
    reset,
  } = useMutation({
    mutationFn: postRequestResetPass,
    onSuccess: (data: any) => {
      setStepTwo(true);
    },
    onError: (error: AxiosError<{ message: string }>) => {
      const msg = error?.response?.data?.message || "خطا در ارسال کد تأیید";
      toast.error(msg);
    },
  });

  const onSubmit = async (data: FormData) => {
    setUserData({
      phone: data.phone,
      nID: data.username,
    });
    const requestBody: any = {
      mobile: valuePhone,
      nID: valueUserName,
      patternID: 42,
    };
    fetchRequestResetPass(requestBody);
  };

  const handleOptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedOption(event.target.value);
  };

  const submitDisabled = isPending;

  return (
    <StackKit
      justifyContent={"center"}
      alignItems={"center"}
      gap={2}
      sx={{ width: { xs: "100%", sm: "100%", md: "80%" } }}
    >
      <img src={"./saee-header-login.svg"} alt={"saee-header-login"} />
      <TypoGraphyKit variant="subtitle1" sx={{ fontSize: "24px" }}>
        فراموشی رمز عبور کلینیک ساعی
      </TypoGraphyKit>
      {stepTwo ? (
        <ForgetPasswordOtp
          setIsForgetPassword={setIsForgetPassword}
          userData={userData}
          valuePhone={valuePhone}
          valueUserName={valueUserName}
        />
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} style={{ width: "100%" }}>
          <StackKit
            justifyContent={"center"}
            alignItems={"center"}
            gap={2}
            sx={{ width: "100%" }}
          >
            <TextField
              {...register("phone")}
              value={valuePhone ? toPersianNumber(valuePhone) : ""}
              placeholder="شماره موبایل خود را وارد کنید"
              sx={{
                width: "60%",
              }}
              onChange={(e) => {
                const englishNumber = toEnglishNumber(e.target.value);
                const rawValue = englishNumber.replace(/[^0-9]/g, "");
                if (rawValue.length <= 11) {
                  setValuePhone(rawValue);
                }
              }}
            />
            {errors.phone && (
              <TypoGraphyKit
                variant="body2"
                sx={{ color: "#ff6b6b", fontSize: "12px" }}
              >
                {errors.phone.message}
              </TypoGraphyKit>
            )}
            <TextField
              {...register("username")}
              placeholder="نام کاربری خود را وارد کنید.( کد ملی)"
              value={valueUserName ? toPersianNumber(valueUserName) : ""}
              sx={{
                width: "60%",
              }}
              onChange={(e) => {
                const englishNumber = toEnglishNumber(e.target.value);
                const rawValue = englishNumber.replace(/[^0-9]/g, "");
                if (rawValue.length <= 11) {
                  setValueUserName(rawValue);
                }
              }}
            />
            <RadioGroup
              value={selectedOption}
              onChange={handleOptionChange}
              sx={{
                gap: 2,
                width: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <StackKit
                direction={"row"}
                justifyContent={"space-between"}
                gap={2}
                sx={{
                  width: "60%",
                  display: "flex",
                  flexDirection: { xs: "column", sm: "column", md: "row" },
                }}
              >
                <FormControlLabel
                  value={USER_TYPES.USER}
                  control={<Radio sx={{ color: "white" }} />}
                  label={
                    <TypoGraphyKit sx={{ color: "white" }}>
                      ورود به پنل کاربر
                    </TypoGraphyKit>
                  }
                />
                {/*<FormControlLabel*/}
                {/*  value={USER_TYPES.MERCHANT}*/}
                {/*  control={<Radio sx={{ color: "white" }} />}*/}
                {/*  label={*/}
                {/*    <TypoGraphyKit sx={{ color: "white" }}>*/}
                {/*      ورود به پنل پذیرنده*/}
                {/*    </TypoGraphyKit>*/}
                {/*  }*/}
                {/*/>*/}
              </StackKit>
            </RadioGroup>
            {errors.username && (
              <TypoGraphyKit
                variant="body2"
                sx={{ color: "#ff6b6b", fontSize: "12px" }}
              >
                {errors.username.message}
              </TypoGraphyKit>
            )}

            {serverError && (
              <TypoGraphyKit sx={{ color: "red", fontSize: "0.9rem" }}>
                {serverError}
              </TypoGraphyKit>
            )}
            <CustomButton
              type="submit"
              sx={{
                backgroundImage:
                  "linear-gradient(90deg, #F4D7D3 21.62%, #78B9C1 87.47%) !important",
                width: "60%",
                height: "48px",

                "&:hover": {
                  opacity: 0.9,
                  background:
                    "linear-gradient(90deg, #F4D7D3 21.62%, #78B9C1 87.47%)",
                },
              }}
            >
              {isPending ? (
                <CircularProgress sx={{ color: "#fff" }} size={24} />
              ) : (
                <TypoGraphyKit
                  variant="body1"
                  fontWeight="Bold"
                  fontSize="24px"
                >
                  ادامه
                </TypoGraphyKit>
              )}
            </CustomButton>
          </StackKit>
        </form>
      )}
      <StackKit alignItems={"center"} sx={{ width: "40%" }}>
        {/* Empty stack - you can add content here if needed */}
      </StackKit>
    </StackKit>
  );
};

export default FormForgetPassword;
